import React, { useState } from 'react';
import { AlertTriangle, X, Send } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Checkbox } from './ui/checkbox';
import { toast } from '../hooks/use-toast';

const ReportTrainerModal = ({ isOpen, onClose, trainer, onReportSuccess }) => {
  const [selectedReasons, setSelectedReasons] = useState([]);
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const API_BASE = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');

  const reportReasons = [
    { id: 'fake_profile', label: '🚫 Fake Profile', description: 'This trainer appears to be using false information' },
    { id: 'inappropriate_behavior', label: '⚠️ Inappropriate Behavior', description: 'Unprofessional or inappropriate conduct' },
    { id: 'harassment', label: '😠 Harassment', description: 'Unwanted or threatening behavior' },
    { id: 'fraud', label: '💰 Fraud', description: 'Fraudulent activity or scam' },
    { id: 'other', label: '📝 Other', description: 'Other reason not listed above' }
  ];

  const handleReasonToggle = (reasonId) => {
    setSelectedReasons(prev => {
      if (prev.includes(reasonId)) {
        return prev.filter(r => r !== reasonId);
      } else {
        return [...prev, reasonId];
      }
    });
  };

  const handleSubmit = async () => {
    // Validation
    if (selectedReasons.length === 0) {
      toast({
        title: 'Select Reason',
        description: 'Please select at least one report reason',
        variant: 'destructive'
      });
      return;
    }

    if (!description.trim()) {
      toast({
        title: 'Description Required',
        description: 'Please write a description for the report',
        variant: 'destructive'
      });
      return;
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      
      // If multiple reasons, combine them; if single reason, use directly
      const primaryReason = selectedReasons[0];
      const allReasons = selectedReasons.join(', ');
      const fullDescription = selectedReasons.length > 1 
        ? `Reasons: ${allReasons}\n\n${description}`
        : description;

      const response = await fetch(`${API_BASE}/api/trainers/report`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          trainer_id: trainer.id,
          reason: primaryReason,
          description: fullDescription
        })
      });

      if (response.ok) {
        toast({
          title: '✅ Report Submitted',
          description: 'Your report will be reviewed by admin. Thank you.'
        });
        
        // Mark booking as reported (if booking_id exists)
        if (trainer.booking_id) {
          try {
            await fetch(`${API_BASE}/api/bookings/${trainer.booking_id}/mark-reported`, {
              method: 'PUT',
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              }
            });
          } catch (err) {
            console.error('Failed to mark booking as reported:', err);
          }
        }
        
        // Reset form
        setSelectedReasons([]);
        setDescription('');
        
        if (onReportSuccess) onReportSuccess(trainer.booking_id);
        onClose();
      } else {
        const error = await response.json();
        toast({
          title: 'Error',
          description: error.detail || 'Failed to submit report',
          variant: 'destructive'
        });
      }
    } catch (error) {
      console.error('Error submitting report:', error);
      toast({
        title: 'Error',
        description: 'Failed to submit report. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setSelectedReasons([]);
    setDescription('');
    onClose();
  };

  if (!trainer) return null;

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center gap-3 justify-center">
            <AlertTriangle className="w-6 h-6 text-red-500" />
            <DialogTitle className="text-xl font-bold">
              Report Trainer
            </DialogTitle>
          </div>
          <DialogDescription className="text-center pt-2">
            Report about <span className="font-semibold text-gray-900 dark:text-gray-100">{trainer.name}</span>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Question Header */}
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
            <h3 className="font-semibold text-red-900 dark:text-red-200 text-center">
              Why do you want to report this trainer?
            </h3>
          </div>

          {/* Reason Checkboxes */}
          <div className="space-y-3">
            <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Report Reasons (Select at least one):
            </p>
            {reportReasons.map((reason) => (
              <div 
                key={reason.id}
                className={`flex items-start gap-3 p-3 rounded-lg border-2 transition-all cursor-pointer ${
                  selectedReasons.includes(reason.id)
                    ? 'border-red-500 bg-red-50 dark:bg-red-900/20'
                    : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                }`}
                onClick={() => handleReasonToggle(reason.id)}
              >
                <Checkbox
                  checked={selectedReasons.includes(reason.id)}
                  onCheckedChange={() => handleReasonToggle(reason.id)}
                  className="mt-1"
                />
                <div className="flex-1">
                  <label className="font-medium text-gray-900 dark:text-gray-100 cursor-pointer block">
                    {reason.label}
                  </label>
                  <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                    {reason.description}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* Description Textarea */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Description (Please explain in detail) *
            </label>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Please explain in detail why you are reporting this trainer. This information will be reviewed by admin..."
              rows={6}
              className="w-full"
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              ⚠️ False reports may affect your account. Please provide accurate information.
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-2 pt-4 border-t dark:border-gray-700">
            <Button
              onClick={handleClose}
              variant="outline"
              className="flex-1"
              disabled={submitting}
            >
              <X className="w-4 h-4 mr-2" />
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={submitting || selectedReasons.length === 0}
              className="flex-1 bg-red-600 hover:bg-red-700 text-white"
            >
              {submitting ? (
                <>Submitting...</>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Submit Report
                </>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ReportTrainerModal;
